import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Loader2, CalendarDays, Users, Clock, MapPin, Mail, Download, CheckCircle2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useLang } from "../i18n/LanguageContext";
import { API, BACKEND_URL, TRIP_META } from "../lib/config";
import { PaymentBadges } from "./Icons";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);
const inputCls = "h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-base text-[var(--ink)] outline-none transition focus:border-[var(--coral)] focus:ring-4 focus:ring-[var(--coral)]/15 placeholder:text-slate-300";

const fmtCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(\d{4})(?=\d)/g, "$1 ");
const fmtExp = (v) => { const d = v.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d; };

const PayForm = ({ order, onDone }) => {
  const { t, money, longDate } = useLang();
  const copy = t.trips.items[order.id];
  const [form, setForm] = useState({ name: "", email: "", card: "", exp: "", cvc: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k, fn = (v) => v) => (e) => setForm((f) => ({ ...f, [k]: fn(e.target.value) }));
  const valid = form.name.trim().length > 1 && /\S+@\S+\.\S+/.test(form.email) && form.card.replace(/\s/g, "").length === 16 && form.exp.length === 5 && form.cvc.length >= 3;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true); setError("");
    try {
      const [res] = await Promise.all([
        axios.post(`${API}/bookings`, { trip_id: order.id, date: order.date, people: order.people, name: form.name.trim(), email: form.email.trim() }),
        new Promise((r) => setTimeout(r, 1800)),
      ]);
      onDone(res.data);
    } catch (err) {
      setError(err?.response?.status === 409 ? t.payment.dateGone : t.payment.error);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-6 lg:grid-cols-[1fr_300px]" data-testid="payment-form">
      <div>
        <DialogTitle className="font-display text-2xl font-bold tracking-tight text-[var(--ink)]">{t.payment.title}</DialogTitle>
        <DialogDescription className="mt-1.5 text-sm text-[var(--mist)]">{t.payment.sub}</DialogDescription>
        <div className="mt-6 grid gap-4">
          <Field label={t.payment.name}><input className={inputCls} value={form.name} onChange={set("name")} placeholder="Alex Morgan" autoComplete="name" data-testid="pay-name" /></Field>
          <Field label={t.payment.email}><input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="alex@example.com" autoComplete="email" data-testid="pay-email" /></Field>
          <Field label={t.payment.card}>
            <div className="relative">
              <input className={`${inputCls} font-mono pr-12`} inputMode="numeric" value={form.card} onChange={set("card", fmtCard)} placeholder="4242 4242 4242 4242" autoComplete="cc-number" data-testid="pay-card" />
              <Lock className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.payment.expiry}><input className={`${inputCls} font-mono`} inputMode="numeric" value={form.exp} onChange={set("exp", fmtExp)} placeholder="MM/YY" autoComplete="cc-exp" data-testid="pay-exp" /></Field>
            <Field label={t.payment.cvc}><input className={`${inputCls} font-mono`} inputMode="numeric" value={form.cvc} onChange={set("cvc", (v) => v.replace(/\D/g, "").slice(0, 4))} placeholder="123" autoComplete="cc-csc" data-testid="pay-cvc" /></Field>
          </div>
        </div>
        {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="pay-error">{error}</p>}
        <button type="submit" disabled={!valid || busy} className="btn-coral mt-6 h-14 w-full text-base" data-testid="pay-submit">
          {busy ? <><Loader2 className="h-5 w-5 animate-spin" />{t.payment.processing}</> : `${t.payment.pay} ${money(order.total)}`}
        </button>
        <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400"><Lock className="h-3 w-3" />{t.payment.secure}</p>
        <p className="mt-1 text-center text-[11px] text-slate-400">{t.payment.demo}</p>
      </div>
      <aside className="rounded-2xl bg-slate-50 p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t.payment.summary}</p>
        <img src={TRIP_META[order.id].image} alt="" className="mt-3 aspect-[16/10] w-full rounded-xl object-cover" />
        <p className="font-display mt-3 text-lg font-bold leading-tight text-[var(--ink)]">{copy.title}</p>
        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[var(--coral)]" /><span className="block first-letter:uppercase">{longDate(order.date)}</span></li>
          <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-[var(--coral)]" />{TRIP_META[order.id].time}</li>
          <li className="flex items-center gap-2"><Users className="h-4 w-4 text-[var(--coral)]" />{order.people} × {money(TRIP_META[order.id].price)}</li>
        </ul>
        <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
          <span className="text-sm font-semibold text-slate-500">{t.trips.total}</span>
          <span className="font-display text-2xl font-extrabold text-[var(--ink)]" data-testid="pay-total">{money(order.total)}</span>
        </div>
        <p className="mt-3 flex items-start gap-1.5 text-xs text-emerald-700"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t.payment.freeCancel}</p>
        <div className="mt-4"><PaymentBadges /></div>
      </aside>
    </form>
  );
};

const Voucher = ({ booking, onClose }) => {
  const { t, money, longDate } = useLang();
  const copy = t.trips.items[booking.trip_id];
  const qrValue = `${BACKEND_URL}/api/bookings/${booking.code}`;
  return (
    <div id="voucher-print" data-testid="voucher">
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18 }} className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-8 w-8" /></motion.div>
      <p className="eyebrow mt-4 text-center !text-emerald-600">{t.voucher.eyebrow}</p>
      <DialogTitle className="font-display mt-2 text-center text-3xl font-extrabold tracking-tight text-[var(--ink)]">{t.voucher.title}</DialogTitle>
      <DialogDescription className="mt-2 flex items-center justify-center gap-1.5 text-center text-sm text-[var(--mist)]"><Mail className="h-4 w-4" />{t.voucher.sent} <strong className="text-[var(--ink)]" data-testid="voucher-email">{booking.email}</strong></DialogDescription>

      <div className="mt-6 overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-white">
        <div className="grid sm:grid-cols-[auto_1fr]">
          <div className="flex flex-col items-center justify-center gap-3 bg-slate-50 p-6">
            <div className="rounded-xl bg-white p-3 shadow-sm" data-testid="voucher-qr"><QRCodeSVG value={qrValue} size={148} level="M" fgColor="#0B1120" /></div>
            <p className="font-mono text-lg font-bold tracking-[0.12em] text-[var(--ink)]" data-testid="voucher-code">{booking.code}</p>
            <p className="max-w-[180px] text-center text-[11px] leading-snug text-slate-500">{t.voucher.show}</p>
          </div>
          <div className="p-6">
            <p className="font-display text-xl font-bold tracking-tight text-[var(--ink)]">{copy.title}</p>
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div><dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t.voucher.date}</dt><dd className="mt-0.5 font-semibold first-letter:uppercase text-[var(--ink)]" data-testid="voucher-date">{longDate(booking.date)}</dd></div>
              <div><dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t.voucher.time}</dt><dd className="mt-0.5 font-semibold text-[var(--ink)]">{booking.time}</dd></div>
              <div><dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t.voucher.people}</dt><dd className="mt-0.5 font-semibold text-[var(--ink)]" data-testid="voucher-people">{booking.people}</dd></div>
              <div><dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t.voucher.paid}</dt><dd className="mt-0.5 font-semibold text-emerald-600" data-testid="voucher-total">{money(booking.total)}</dd></div>
              <div className="col-span-2"><dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t.voucher.meeting}</dt><dd className="mt-0.5 flex items-center gap-1.5 font-semibold text-[var(--ink)]"><MapPin className="h-4 w-4 text-[var(--coral)]" />{copy.meeting}</dd></div>
            </dl>
            <p className="mt-4 text-xs text-slate-400">{booking.name} · {t.voucher.code} {booking.code}</p>
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button type="button" onClick={() => window.print()} className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white text-sm font-semibold text-[var(--ink)] transition hover:bg-slate-50" data-testid="voucher-download"><Download className="h-4 w-4" />{t.voucher.download}</button>
        <button type="button" onClick={onClose} className="btn-coral h-12 flex-1 text-sm" data-testid="voucher-done">{t.voucher.bookAnother}</button>
      </div>
    </div>
  );
};

export const PaymentDialog = ({ order, onClose, onBooked }) => {
  const [booking, setBooking] = useState(null);
  useEffect(() => { if (!order) setBooking(null); }, [order]);
  const done = (b) => { setBooking(b); onBooked(b); };
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent className="max-h-[92svh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto rounded-3xl border-0 bg-white p-6 shadow-2xl sm:p-8" data-testid="payment-dialog">
        <AnimatePresence mode="wait" initial={false}>
          {order && !booking && (
            <motion.div key="pay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}><PayForm order={order} onDone={done} /></motion.div>
          )}
          {booking && (
            <motion.div key="voucher" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}><Voucher booking={booking} onClose={onClose} /></motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
